// Pure data — no icon component values, so this stays server-safe.

export const QUICK_ANSWER =
  "Choose Proxidize for premium pooled mobile IPs at a flat $2/GB — well under IPRoyal's rotating mobile rate of $5.20–6.80/GB — and for lower-cost residential bandwidth at scale. Choose IPRoyal for a broader proxy platform (ISP, datacenter, Web Unblocker, a scraping API) or for very small one-off mobile and residential tests below Proxidize's minimum purchase.";

export const KEY_TAKEAWAYS = [
  "IPRoyal's rotating mobile rate runs well above Proxidize's flat $2/GB. $5.20–6.80/GB by volume, versus Proxidize's flat rate at every plan size.",
  "IPRoyal has a smaller mobile testing entry point. A 24-hour per-proxy plan or 2 GB rotating bundle, versus Proxidize's 25 GB minimum.",
  "IPRoyal residential runs $4.90–7.00/GB on published subscription tiers. Proxidize's flat $1/GB is cheaper at every volume checked — $50 vs $245 at 50 GB, for example.",
  "IPRoyal sells a broader platform. ISP, datacenter, Web Unblocker, and a Video Scraper API alongside proxies; Proxidize is proxy-focused.",
  "Both providers advertise bandwidth that isn't wasted. IPRoyal's traffic never expires; Proxidize rolls over unused bandwidth instead of letting it expire.",
  "Proxidize gives more control over mobile session length. Sticky sessions per access point plus manual and API rotation, versus IPRoyal's plan-dependent rotation behavior.",
];

export interface VerdictCard {
  provider: "proxidize" | "iproyal";
  title: string;
  points: string[];
}

export const VERDICT_CARDS: VerdictCard[] = [
  {
    provider: "proxidize",
    title: "Choose Proxidize if you need",
    points: [
      "Premium pooled mobile IPs across multiple countries with city-level targeting",
      "Mobile bandwidth at a flat $2/GB — well under IPRoyal's rotating mobile rate of $5.20/GB at 100 GB",
      "A per-proxy mobile plan from $59/proxy/month with unlimited bandwidth, cheaper than IPRoyal's dedicated mobile plan at $130/month (30-day)",
      "Residential proxies in 195+ countries at a flat $1/GB with unused bandwidth rollover",
      "Manual/bulk rotation, an API rotation URL, and custom interval control",
      "Self-serve setup: credentials in under a minute, no sales call",
    ],
  },
  {
    provider: "iproyal",
    title: "Choose IPRoyal if you need",
    points: [
      "A broader proxy platform: residential, ISP, datacenter, mobile, Web Unblocker, Video Scraper API, and enterprise proxies",
      "Very small paid mobile tests — a 24-hour per-proxy plan or a 2 GB rotating bundle",
      "Residential traffic that never expires",
      "Broader global mobile coverage with country, state, and city targeting",
      "One vendor for proxies plus scraping/unblocking tooling",
    ],
  },
];

export interface ComparisonRow {
  feature: string;
  proxidize: string | boolean;
  iproyal: string | boolean;
}

export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    feature: "Best for",
    proxidize: "Mobile IPs, self-serve simplicity",
    iproyal: "Broad proxy platform with mobile, residential, ISP, datacenter, and scraping tools",
  },
  {
    feature: "Product breadth",
    proxidize: "Mobile and residential live; ISP and datacenter coming soon",
    iproyal: "Residential, ISP, Datacenter, Mobile, Web Unblocker, Video Scraper API, Enterprise",
  },
  {
    feature: "Mobile network",
    proxidize: "5G/4G/3G/LTE premium pooled mobile IPs, 50+ locations across multiple countries",
    iproyal: "5G/4G/3G/LTE mobile IPs, per-proxy and rotating",
  },
  {
    feature: "Mobile country coverage",
    proxidize: "US, Canada, UK, Germany, India",
    iproyal: "Global (country, state, city targeting)",
  },
  {
    feature: "Mobile targeting",
    proxidize: "City + ASN",
    iproyal: "Country, state, city, and network",
  },
  {
    feature: "Rotating mobile pricing",
    proxidize: "Flat $2/GB (plans from 25 GB to 10,000 GB)",
    iproyal: "$6.80 down to $5.20/GB by volume (2 GB to 100 GB)",
  },
  {
    feature: "100 GB rotating mobile",
    proxidize: "$200/mo ($2.00/GB)",
    iproyal: "$520/mo ($5.20/GB)",
  },
  {
    feature: "Smallest mobile entry point",
    proxidize: "25 GB for $50",
    iproyal: "24-hour per-proxy from $10.11/day, or a 2 GB rotating bundle from $13.60",
  },
  {
    feature: "Per-proxy mobile pricing",
    proxidize: "From $59/proxy/month, unlimited bandwidth",
    iproyal: "From $117–$130/proxy/month on 30–90 day plans",
  },
  {
    feature: "Mobile data limits",
    proxidize: "50 GB prioritized data/month per proxy, then deprioritized (stays active)",
    iproyal: "30 GB/day on per-proxy plans; no daily limit on rotating plans",
  },
  {
    feature: "Sticky sessions",
    proxidize: "Sticky or random per access point",
    iproyal: "Sticky sessions supported; rotation behavior depends on plan",
  },
  {
    feature: "Rotation control",
    proxidize: "Custom interval (min 1 min), manual, API rotation URL",
    iproyal: "Manual on 1-day plans; automatic every 6 minutes on 30/60/90-day plans",
  },
  {
    feature: "Protocols",
    proxidize: "HTTP(S), SOCKS5, HTTP/3 + QUIC",
    iproyal: "HTTP(S), SOCKS5",
  },
  { feature: "Self-serve signup", proxidize: true, iproyal: true },
  {
    feature: "Free / low-cost testing",
    proxidize: "Free sign-up, no credit card",
    iproyal: "24-hour per-proxy plan and 2 GB rotating bundle for paid testing",
  },
  {
    feature: "Residential network",
    proxidize: "Millions of IPs, 195+ countries, ethically sourced",
    iproyal: "Large residential pool with never-expiring traffic",
  },
  {
    feature: "Residential pricing",
    proxidize: "Flat $1/GB (plans from 25 GB to 10,000 GB)",
    iproyal: "$7.00 down to $4.90/GB by volume (1 GB to 50 GB, subscription pricing)",
  },
  {
    feature: "50 GB residential plan",
    proxidize: "$50/mo ($1.00/GB)",
    iproyal: "$245/mo ($4.90/GB)",
  },
  {
    feature: "Unused residential bandwidth",
    proxidize: "Rolls over, doesn't expire",
    iproyal: "Traffic never expires",
  },
  {
    feature: "ISP (static residential) proxies",
    proxidize: "Not yet offered",
    iproyal: "Available",
  },
  { feature: "Datacenter proxies", proxidize: "Not yet offered", iproyal: "Available" },
  {
    feature: "Scraping / unblocking tooling",
    proxidize: false,
    iproyal: "Web Unblocker, Video Scraper API",
  },
];

export interface UseCaseFit {
  useCase: string;
  fit: "Proxidize" | "IPRoyal" | "Both";
  reason: string;
}

export const USE_CASE_FITS: UseCaseFit[] = [
  {
    useCase: "Mobile IP workflows in Proxidize's covered countries",
    fit: "Proxidize",
    reason:
      "Premium pooled mobile IPs with city-level targeting, plus per-GB and per-proxy pricing built specifically for this workflow.",
  },
  {
    useCase: "Very small or one-off mobile testing",
    fit: "IPRoyal",
    reason:
      "A 24-hour per-proxy plan and a 2 GB rotating bundle fit tiny paid tests better than Proxidize's 25 GB minimum.",
  },
  {
    useCase: "Stable mobile exits for account-based workflows",
    fit: "Proxidize",
    reason:
      "Per-proxy plans include unlimited bandwidth, manual/bulk rotation, a rotation URL, and interval control, at a lower listed monthly price.",
  },
  {
    useCase: "Broad proxy platform under one account",
    fit: "IPRoyal",
    reason:
      "Residential, ISP, datacenter, mobile, Web Unblocker, Video Scraper API, and enterprise proxies in one place.",
  },
  {
    useCase: "Lower-cost residential bandwidth at scale",
    fit: "Proxidize",
    reason: "A flat $1/GB from 25 GB beats IPRoyal's visible $4.90–$7.00/GB subscription tiers.",
  },
  {
    useCase: "Residential bandwidth that doesn't go to waste",
    fit: "Both",
    reason:
      "IPRoyal promotes never-expiring traffic; Proxidize rolls over unused bandwidth instead of letting it expire.",
  },
];

export interface MigrationStep {
  title: string;
  description: string;
}

export const MIGRATION_STEPS: MigrationStep[] = [
  {
    title: "Create a free account",
    description: "Sign up for free: no credit card, no sales call.",
  },
  {
    title: "Pick per-GB or per-proxy",
    description:
      "Per-GB gives flexible access to the full pool billed by data. Per-proxy gives you real mobile IPs of your own with unlimited bandwidth.",
  },
  {
    title: "Create an access point",
    description:
      "Choose a city or random rotation and set sticky or random IP behavior — the same knobs you tuned in your old dashboard.",
  },
  {
    title: "Copy your credentials",
    description:
      "Credentials are generated live in HTTP or SOCKS5 format, with auto-generated cURL examples and formatted proxy list exports.",
  },
  {
    title: "Test before switching traffic",
    description:
      "Run your existing scraper or browser profile against the new endpoint with a small job and compare success rates side by side.",
  },
  {
    title: "Monitor usage in real time",
    description:
      "The built-in analytics dashboard shows bandwidth trends, top domains, and a live connection log per access point.",
  },
];

export const MIGRATION_CURL = `curl -x "http://USERNAME:PASSWORD@HOST:PORT" https://ipinfo.io`;
